import type { CogoFlow, CogoFlowStep, CogoBasicStep, CogoConditionalStep, CogoLoopStep, CogoParallelStep, CogoTryCatchStep, CogoSwitchStep } from "@cogo/types"
import type { GeminiContent } from "./gemini/gemini-client"
import { StateManager } from "./state-manager"
import { ComputeEvaluator, isTruthy } from "./compute-evaluator"
import type { FunctionRegistry } from "./expression-function"
import { NavigationHistoryStack } from "./navigation-history"
import type { PlatformAdapter } from "./platform-adapter"

export type ToastHandler = (params: { message: string; description?: string; variant?: string }) => void

interface WsConnection {
  send(data: string): void
  close(): void
  onMessage(handler: (data: unknown) => void): void
  onClose(handler: () => void): void
}

export class ActionFlowEngine {
  private state: StateManager
  private compute: ComputeEvaluator
  private builtinActions: Record<string, (params: Record<string, unknown>, context?: Record<string, unknown>) => void | Promise<void>>
  private debounceTimers = new Map<string, ReturnType<typeof setTimeout>>()
  private functionRegistry: FunctionRegistry | null = null
  private flowRegistry: Record<string, CogoFlow> = {}
  private platform: PlatformAdapter | null = null

  /** Active WebSocket connections keyed by connection ID */
  private wsConnections = new Map<string, WsConnection>()
  private actionQueues = new Map<string, Array<() => Promise<void>>>()
  private throttleTimestamps = new Map<string, number>()
  private actionDebounceTimers = new Map<string, ReturnType<typeof setTimeout>>()

  /** External toast handler set by the host app */
  onToast: ToastHandler | null = null

  /** External handler for incoming WebSocket messages */
  onWebSocketMessage: ((id: string, data: unknown) => void) | null = null

  /** External handler for form validation errors */
  onValidationError: ((errors: Record<string, string>) => void) | null = null

  /** External handler for showing popup/modal */
  onShowPopup: ((params: { title: string; message: string; type?: string }) => void) | null = null

  /** External handler for showing/hiding loading overlay */
  onShowLoading: ((show: boolean) => void) | null = null

  /** External handler for payment processing */
  onPayment: ((params: { provider: string; amount: number; currency: string; metadata?: Record<string, unknown> }) => Promise<{ success: boolean; transactionId?: string; error?: string }>) | null = null

  /** External handler for web3 wallet operations */
  onWeb3: ((params: { action: string; chainId?: number; to?: string; value?: string; data?: string }) => Promise<{ success: boolean; result?: unknown; error?: string }>) | null = null

  /** External handler for Firebase Auth operations */
  onFirebaseAuth: ((params: { action: string; email?: string; password?: string; token?: string }) => Promise<{ success: boolean; user?: unknown; error?: string }>) | null = null

  /** External handler for Firestore operations */
  onFirestore: ((params: { operation: string; collection: string; docId?: string; data?: Record<string, unknown> }) => Promise<{ success: boolean; data?: unknown; error?: string }>) | null = null

  private _navHistory: NavigationHistoryStack | null = null

  /** Lazily-initialized navigation history stack. */
  get navigationHistory(): NavigationHistoryStack {
    if (!this._navHistory) {
      this._navHistory = new NavigationHistoryStack("/")
    }
    return this._navHistory
  }

  constructor(state: StateManager, platform?: PlatformAdapter) {
    this.state = state
    this.compute = state.getCompute()
    this.platform = platform ?? null
    this.builtinActions = {
      setState: (params) => this.actionSetState(params),
      navigate: (params) => this.actionNavigate(params),
      showToast: (params) => this.actionShowToast(params),
      delay: (params) => this.actionDelay(params),
      websocket: (params) => this.actionWebSocket(params),
      formValidate: (params) => this.actionFormValidate(params),
      scrollTo: (params) => this.actionScrollTo(params),
      payment: (params) => this.actionPayment(params),
      web3: (params) => this.actionWeb3(params),
      restApi: (params) => this.actionRestApi(params),
      graphqlAction: (params) => this.actionGraphql(params),
      supabaseQuery: (params) => this.actionSupabaseQuery(params),
      supabaseRpc: (params) => this.actionSupabaseRpc(params),
      setVariable: (params) => this.actionSetVariable(params),
      log: (params) => this.actionLog(params),
      stopFlow: () => this.actionStopFlow(),
      batchAction: (params) => this.actionBatch(params),
      conditionalDelay: (params) => this.actionConditionalDelay(params),
      cryptoAction: (params) => this.actionCrypto(params),
      realtimeSync: (params) => this.actionRealtimeSync(params),
      analyticsEvent: (params) => this.actionAnalyticsEvent(params),
      goLink: (params) => this.actionGoLink(params),
      showPopup: (params) => this.actionShowPopup(params),
      showLoading: (params) => this.actionShowLoading(params),
      storage: (params) => this.actionStorage(params),
      copyToClipboard: (params) => this.actionCopyToClipboard(params),
      clipboardRead: (params) => this.actionClipboardRead(params),
      notification: (params) => this.actionNotification(params),
      shareAction: (params) => this.actionShare(params),
      printAction: () => this.actionPrint(),
      downloadAction: (params) => this.actionDownload(params),
      csvExport: (params) => this.actionCsvExport(params),
      fileUpload: (params) => this.actionFileUpload(params),
      mediaCapture: (params) => this.actionMediaCapture(params),
      geolocation: (params) => this.actionGeolocation(params),
      firebaseAuth: (params) => this.actionFirebaseAuth(params),
      firestoreAction: (params) => this.actionFirestore(params),
      queueAction: (params) => this.actionQueue(params),
      retryAction: (params) => this.actionRetry(params),
      throttleAction: (params) => this.actionThrottle(params),
      debounceAction: (params) => this.actionDebounce(params),
      transactionAction: (params) => this.actionTransaction(params),
      geminiAction: (params) => this.actionGemini(params),
      callFlow: (params, ctx) => this.actionCallFlow(params, ctx),
      simulateError: (params) => this.actionSimulateError(params),
      compute: (params) => this.actionCompute(params),
    }
  }

  /** Set platform adapter for browser/RN-specific operations. */
  setPlatform(platform: PlatformAdapter): void {
    this.platform = platform
  }

  /** Set function registry for expression function calls. */
  setFunctionRegistry(registry: FunctionRegistry): void {
    this.functionRegistry = registry
  }

  /** Register named flows so callFlow action can invoke sub-flows. */
  registerFlows(flows: Record<string, CogoFlow>): void {
    Object.assign(this.flowRegistry, flows)
  }

  /**
   * Execute all steps in a flow.
   * Supports flow-level debounce: if flow.debounce > 0, the execution is
   * delayed and subsequent calls within the debounce window cancel the previous.
   * @param flow - The flow definition
   * @param context - Execution context (event payload, item variables, etc.)
   */
  async executeFlow(flow: CogoFlow, context?: Record<string, unknown>): Promise<void> {
    if (flow.debounce && flow.debounce > 0 && flow.name) {
      return new Promise<void>((resolve) => {
        const existing = this.debounceTimers.get(flow.name!)
        if (existing) clearTimeout(existing)
        this.debounceTimers.set(
          flow.name!,
          setTimeout(async () => {
            this.debounceTimers.delete(flow.name!)
            await this.executeFlowImmediate(flow, context)
            resolve()
          }, flow.debounce),
        )
      })
    }
    return this.executeFlowImmediate(flow, context)
  }

  /** Clear all pending debounce timers. Useful for cleanup/tests. */
  clearDebounceTimers(): void {
    for (const timer of this.debounceTimers.values()) {
      clearTimeout(timer)
    }
    this.debounceTimers.clear()
  }

  private async executeFlowImmediate(flow: CogoFlow, context?: Record<string, unknown>): Promise<void> {
    try {
      for (const step of flow.steps) {
        await this.executeStep(step, context ?? {})
      }
    } catch (e) {
      if (e && typeof e === "object" && (e as Record<string, unknown>).__stopFlow) return
      throw e
    }
  }

  registerAction(
    name: string,
    handler: (params: Record<string, unknown>) => void | Promise<void>,
  ): void {
    this.builtinActions[name] = handler
  }

  private async executeStep(step: CogoFlowStep, context: Record<string, unknown>): Promise<void> {
    switch (step.type || "basic") {
      case "basic":
        await this.executeBasic(step as CogoBasicStep, context)
        break
      case "conditional":
        await this.executeConditional(step as CogoConditionalStep, context)
        break
      case "expression": {
        const exprStep = step as unknown as CogoBasicStep
        await this.executeBasic(exprStep, context)
        break
      }
      case "loop":
        await this.executeLoop(step as CogoLoopStep, context)
        break
      case "parallel":
        await this.executeParallel(step as CogoParallelStep, context)
        break
      case "tryCatch":
        await this.executeTryCatch(step as CogoTryCatchStep, context)
        break
      case "switch":
        await this.executeSwitch(step as CogoSwitchStep, context)
        break
    }
  }

  private async executeBasic(step: CogoBasicStep, context: Record<string, unknown>): Promise<void> {
    // For setState/upsert, selectively resolve: keep onMatch raw so
    // StateManager can evaluate #{item.*} expressions with the matched element.
    const rawParams = step.params
    const resolvedParams = this.resolveParams(rawParams, context)

    // Restore raw onMatch — it contains #{item.*} that can only be resolved
    // inside upsertWhere() where the matched array element is available.
    if (step.action === "setState" && rawParams.operation === "upsert" && rawParams.onMatch) {
      resolvedParams.onMatch = rawParams.onMatch
    }

    // Restore raw expression — it contains code (e.g., .find(), .slice(), .filter())
    // that must be evaluated by ComputeEvaluator, not pre-resolved as a state path.
    if (step.action === "compute" && rawParams.expression) {
      let expr = String(rawParams.expression)
      // Strip #{...} wrapper — ComputeEvaluator expects unwrapped expressions
      if (expr.startsWith("#{") && expr.endsWith("}")) {
        expr = expr.slice(2, -1)
      }
      resolvedParams.expression = this.resolveEventRefs(expr, context)
    }

    const action = this.builtinActions[step.action]
    if (action) {
      try {
        await action(resolvedParams, context)
      } catch (e) {
        if (e && typeof e === "object" && (e as Record<string, unknown>).__stopFlow) throw e
        throw e
      }
    } else {
      console.warn(`Unknown action: ${step.action}`)
    }
  }

  private async executeConditional(
    step: CogoConditionalStep,
    context: Record<string, unknown>,
  ): Promise<void> {
    // Resolve #{...} patterns in condition before evaluating
    const resolvedCondition = String(this.resolveValue(step.condition, context) ?? step.condition)
    const result = this.compute.evaluate(resolvedCondition)
    const branch = isTruthy(result) ? step.then : (step.else ?? [])
    for (const s of branch) {
      await this.executeStep(s, context)
    }
  }

  private async executeLoop(step: CogoLoopStep, context: Record<string, unknown>): Promise<void> {
    const collection = this.resolveValue(step.collection, context)
    if (!Array.isArray(collection)) return
    for (const item of collection) {
      const loopCtx = { ...context, [step.itemVar]: item }
      for (const s of step.steps) {
        await this.executeStep(s, loopCtx)
      }
    }
  }

  private async executeParallel(step: CogoParallelStep, context: Record<string, unknown>): Promise<void> {
    await Promise.all(step.steps.map((s) => this.executeStep(s, context)))
  }

  private async executeTryCatch(step: CogoTryCatchStep, context: Record<string, unknown>): Promise<void> {
    try {
      for (const s of step.try) {
        await this.executeStep(s, context)
      }
    } catch (e) {
      const errorContext = { ...context, error: { message: String(e) } }
      for (const s of step.catch) {
        await this.executeStep(s, errorContext)
      }
    }
  }

  private async executeSwitch(step: CogoSwitchStep, context: Record<string, unknown>): Promise<void> {
    const resolved = String(this.resolveValue(step.value, context) ?? "")
    const branch = step.cases[resolved] ?? step.default ?? []
    for (const s of branch) {
      await this.executeStep(s, context)
    }
  }

  private async actionCallFlow(params: Record<string, unknown>, parentContext?: Record<string, unknown>): Promise<void> {
    const flowName = (params.flow ?? params.flowName) as string
    const flow = this.flowRegistry[flowName]
      ?? Object.entries(this.flowRegistry).find(([k]) => k.endsWith(`.${flowName}`))?.[1]
    if (!flow) return
    const ctx = (params.context as Record<string, unknown>) ?? parentContext ?? {}
    await this.executeFlowImmediate(flow, ctx)
  }

  private actionSimulateError(params: Record<string, unknown>): never {
    throw new Error((params.message as string) ?? "Simulated error")
  }

  // ---- Param Resolution ----

  /** Deep-resolve #{...} patterns in an object tree */
  private resolveParams(params: Record<string, unknown>, ctx: Record<string, unknown>): Record<string, unknown> {
    const result: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(params)) {
      result[key] = this.resolveValue(value, ctx)
    }
    return result
  }

  private resolveValue(value: unknown, ctx: Record<string, unknown>): unknown {
    if (typeof value === "string") {
      // Pure symbol: "#{product.id}"
      const pureMatch = /^#\{([^}]+)\}$/.exec(value)
      if (pureMatch) {
        const inner = pureMatch[1].trim()
        const ctxVal = this.getFromContext(inner, ctx)
        if (ctxVal !== undefined) return ctxVal
        const stateVal = this.state.get(inner)
        if (stateVal !== undefined) return stateVal
        // Expression contains operators — evaluate via ComputeEvaluator
        // e.g., "state.currentPage + 1", "state.sortDir == 'asc' ? 'desc' : 'asc'"
        if (/[+\-*/<>=?!|&]/.test(inner)) {
          return this.compute.evaluate(this.resolveEventRefs(inner, ctx))
        }
        return undefined
      }
      // Mixed template: "Added #{product.name} to cart"
      if (value.includes("#{")) {
        // Code expressions (arrow functions) need string values JSON-quoted
        const isCodeExpr = value.includes("=>")
        return value.replace(/#\{([^}]+)\}/g, (match, ref, offset: number) => {
          const r = (ref as string).trim()
          const ctxVal = this.getFromContext(r, ctx)
          let resolved = ctxVal !== undefined ? ctxVal : this.state.get(r)
          // Fallback: evaluate expressions with operators (e.g., "state.x + 1")
          if (resolved == null && /[+\-*/<>=?!|&]/.test(r)) {
            resolved = this.compute.evaluate(this.resolveEventRefs(r, ctx))
          }
          if (resolved == null) return isCodeExpr ? "null" : ""
          if (isCodeExpr && typeof resolved === "string") {
            // Check if #{...} is already inside quotes — don't double-quote
            const prevChar = offset > 0 ? value[offset - 1] : ""
            const nextChar = value[offset + match.length] ?? ""
            if ((prevChar === "'" && nextChar === "'") || (prevChar === '"' && nextChar === '"')) {
              return String(resolved)
            }
            return JSON.stringify(resolved)
          }
          return String(resolved)
        })
      }
      // Strip lone # prefix for state paths (e.g., "#cart.items")
      if (value.startsWith("#") && !value.includes("{")) {
        return value
      }
      return value
    }
    if (Array.isArray(value)) {
      return value.map((v) => this.resolveValue(v, ctx))
    }
    if (value !== null && typeof value === "object") {
      return this.resolveParams(value as Record<string, unknown>, ctx)
    }
    return value
  }

  /** Replace event.xxx tokens with literal values from the flow context
   *  so ComputeEvaluator (which only knows state) can evaluate the expression. */
  private resolveEventRefs(expr: string, ctx: Record<string, unknown>): string {
    const event = ctx.event
    if (event == null || typeof event !== "object") return expr
    const ev = event as Record<string, unknown>
    return expr.replace(/\bevent\.(\w+)/g, (_m, key) => {
      const val = ev[key as string]
      if (val === undefined) return _m
      if (typeof val === "string") return `'${val}'`
      return String(val)
    })
  }

  private getFromContext(path: string, ctx: Record<string, unknown>): unknown {
    const keys = path.split(".")
    let current: unknown = ctx
    for (const key of keys) {
      if (current == null || typeof current !== "object") return undefined
      current = (current as Record<string, unknown>)[key]
    }
    return current
  }

  // ---- Built-in Actions ----

  private actionSetState(params: Record<string, unknown>): void {
    const { operation, value } = params as {
      target: string
      operation?: string
      value?: unknown
    }
    const target = (params.target as string).replace(/^#/, "")

    switch (operation) {
      case "append":
        this.state.appendTo(target, value)
        break
      case "upsert": {
        const match = params.match as string | undefined
        const onMatch = params.onMatch as Record<string, unknown> | undefined
        const onNew = params.onNew as Record<string, unknown> | undefined
        this.state.upsertWhere(target, match, onMatch, onNew)
        break
      }
      case "removeWhere": {
        const predicate = params.predicate as string | undefined
        if (predicate) {
          this.state.removeWhere(target, (item) =>
            this.compute.evalPredicate(predicate, item),
          )
        }
        break
      }
      case "updateWhere": {
        const pred = params.predicate as string | undefined
        const update = params.update as Record<string, unknown> | undefined
        if (pred && update) {
          this.state.updateWhere(
            target,
            (item) => this.compute.evalPredicate(pred, item),
            update,
          )
        }
        break
      }
      default:
        // Shallow-merge when both current and new values are plain objects.
        // Flow JSONs use setState target:"#ui" with partial object values
        // (e.g., { isLoading: true }) expecting merge, not replacement.
        if (
          value !== null &&
          typeof value === "object" &&
          !Array.isArray(value)
        ) {
          const current = this.state.get(target)
          if (
            current !== null &&
            typeof current === "object" &&
            !Array.isArray(current)
          ) {
            this.state.setState(target, { ...(current as Record<string, unknown>), ...(value as Record<string, unknown>) })
            return
          }
        }
        this.state.setState(target, value)
    }
  }

  private actionNavigate(params: Record<string, unknown>): void {
    if (!this.platform) return
    const mode = (params.mode as string) ?? "push"
    const route = params.route as string | undefined
    const navState = (params.state as Record<string, unknown>) ?? null

    switch (mode) {
      case "replace": {
        if (!route) return
        this.navigationHistory.replace(route, navState ?? undefined)
        this.platform.replaceRoute(route)
        break
      }
      case "back": {
        const entry = this.navigationHistory.back()
        if (entry) {
          this.platform.pushRoute(entry.route)
        }
        break
      }
      case "forward": {
        const entry = this.navigationHistory.forward()
        if (entry) {
          this.platform.pushRoute(entry.route)
        }
        break
      }
      case "push":
      default: {
        if (!route) return
        this.navigationHistory.push(route, navState ?? undefined)
        this.platform.pushRoute(route)
        break
      }
    }

    this.syncNavigationState()
  }

  private syncNavigationState(): void {
    const nav = this.navigationHistory
    this.state.setState("_system.navigation", {
      canGoBack: nav.canGoBack,
      canGoForward: nav.canGoForward,
      currentRoute: nav.currentEntry?.route ?? "/",
    })
  }

  private actionShowToast(params: Record<string, unknown>): void {
    const msg = {
      message: String(params.message ?? ""),
      description: params.description ? String(params.description) : undefined,
      variant: params.variant ? String(params.variant) : undefined,
    }
    if (this.onToast) {
      this.onToast(msg)
    } else if (this.platform) {
      this.platform.showToast(msg)
    } else {
      console.log(`[Toast${msg.variant ? `:${msg.variant}` : ""}] ${msg.message}`)
    }
  }

  private actionDelay(params: Record<string, unknown>): Promise<void> {
    const duration = Number(params.duration ?? 0)
    return new Promise((resolve) => setTimeout(resolve, duration))
  }

  private actionWebSocket(params: Record<string, unknown>): Promise<void> {
    if (!this.platform) return Promise.resolve()
    const id = String(params.id ?? "default")
    const action = String(params.action ?? "connect")
    const url = String(params.url ?? "")

    switch (action) {
      case "connect": {
        if (this.wsConnections.has(id)) return Promise.resolve()
        const ws = this.platform.createWebSocket(url)
        this.wsConnections.set(id, ws)

        ws.onMessage((data: unknown) => {
          if (this.onWebSocketMessage) {
            this.onWebSocketMessage(id, data)
          }
          // Auto-update state if stateTarget is specified
          const stateTarget = params.stateTarget as string | undefined
          if (stateTarget) {
            this.state.setState(stateTarget.replace(/^#/, ""), data)
          }
        })

        ws.onClose(() => {
          this.wsConnections.delete(id)
        })

        return Promise.resolve()
      }
      case "send": {
        const ws = this.wsConnections.get(id)
        if (!ws) return Promise.resolve()
        ws.send(typeof params.data === "string" ? params.data : JSON.stringify(params.data))
        return Promise.resolve()
      }
      case "close": {
        const ws = this.wsConnections.get(id)
        if (ws) {
          ws.close()
          this.wsConnections.delete(id)
        }
        return Promise.resolve()
      }
      default:
        return Promise.resolve()
    }
  }

  /** Clean up all WebSocket connections. */
  closeAllWebSockets(): void {
    for (const ws of this.wsConnections.values()) {
      ws.close()
    }
    this.wsConnections.clear()
  }

  private actionFormValidate(params: Record<string, unknown>): void {
    const rules = params.rules as Record<string, Record<string, unknown>> | undefined
    if (!rules) return

    const errors: Record<string, string> = {}
    for (const [field, rule] of Object.entries(rules)) {
      const value = this.state.get(field)
      const strValue = value == null ? "" : String(value)

      if (rule.required && !strValue) {
        errors[field] = String(rule.message ?? `${field} is required`)
        continue
      }
      if (rule.pattern && strValue) {
        const regex = new RegExp(String(rule.pattern))
        if (!regex.test(strValue)) {
          errors[field] = String(rule.message ?? `${field} is invalid`)
          continue
        }
      }
      if (rule.min != null && strValue.length < Number(rule.min)) {
        errors[field] = String(rule.message ?? `${field} must be at least ${rule.min} characters`)
        continue
      }
      if (rule.max != null && strValue.length > Number(rule.max)) {
        errors[field] = String(rule.message ?? `${field} must be at most ${rule.max} characters`)
        continue
      }
    }

    const errorTarget = String(params.errorTarget ?? "formErrors")
    this.state.setState(errorTarget, Object.keys(errors).length > 0 ? errors : null)

    if (this.onValidationError && Object.keys(errors).length > 0) {
      this.onValidationError(errors)
    }
  }

  private actionScrollTo(params: Record<string, unknown>): void {
    if (!this.platform) return
    const target = params.target as string | undefined
    const smooth = params.behavior !== "instant"
    const offset = Number(params.offset ?? 0)

    if (target) {
      this.platform.scrollToElement(target, smooth)
    } else {
      const top = Number(params.top ?? 0) + offset
      this.platform.scrollToPosition(top, smooth)
    }
  }

  private async actionPayment(params: Record<string, unknown>): Promise<void> {
    const provider = String(params.provider ?? "stripe")
    const amount = Number(params.amount ?? 0)
    const currency = String(params.currency ?? "usd")
    const metadata = params.metadata as Record<string, unknown> | undefined
    const resultTarget = String(params.resultTarget ?? "paymentResult")

    if (this.onPayment) {
      const result = await this.onPayment({ provider, amount, currency, metadata })
      this.state.setState(resultTarget, result)
    } else {
      console.warn("ActionFlowEngine: payment handler not configured")
      this.state.setState(resultTarget, { success: false, error: "Payment handler not configured" })
    }
  }

  private async actionWeb3(params: Record<string, unknown>): Promise<void> {
    const action = String(params.action ?? "connect")
    const chainId = params.chainId != null ? Number(params.chainId) : undefined
    const to = params.to as string | undefined
    const value = params.value as string | undefined
    const data = params.data as string | undefined
    const resultTarget = String(params.resultTarget ?? "web3Result")

    if (this.onWeb3) {
      const result = await this.onWeb3({ action, chainId, to, value, data })
      this.state.setState(resultTarget, result)
    } else {
      console.warn("ActionFlowEngine: web3 handler not configured")
      this.state.setState(resultTarget, { success: false, error: "Web3 handler not configured" })
    }
  }

  // ---- Batch 1: Data & State Actions ----

  private async actionRestApi(params: Record<string, unknown>): Promise<void> {
    const url = String(params.url ?? "")
    const method = String(params.method ?? "GET").toUpperCase()
    const headers = (params.headers as Record<string, string>) ?? {}
    const body = params.body
    const resultTarget = String(params.resultTarget ?? "_results.restApi")

    const credentials = params.credentials as string | undefined

    const fetchOptions: RequestInit = {
      method,
      headers: { "Content-Type": "application/json", ...headers },
    }
    if (credentials) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      fetchOptions.credentials = credentials as any
    }
    if (body && method !== "GET") {
      fetchOptions.body = typeof body === "string" ? body : JSON.stringify(body)
    }

    try {
      const response = await fetch(url, fetchOptions)
      const ct = response.headers.get("content-type") ?? ""
      const data = ct.includes("application/json") ? await response.json() : await response.text()
      const cleanTarget = resultTarget.replace(/^#/, "")
      this.state.setState(cleanTarget, data)
    } catch (err) {
      this.state.setState(resultTarget.replace(/^#/, ""), { error: String(err) })
    }
  }

  private async actionGraphql(params: Record<string, unknown>): Promise<void> {
    const url = String(params.url ?? params.endpoint ?? "")
    const query = String(params.query ?? "")
    const variables = (params.variables as Record<string, unknown>) ?? {}
    const headers = (params.headers as Record<string, string>) ?? {}
    const resultTarget = String(params.resultTarget ?? "_results.graphql")

    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...headers },
        body: JSON.stringify({ query, variables }),
      })
      const json = (await res.json()) as Record<string, unknown>
      if (json.errors) {
        this.state.setState(resultTarget.replace(/^#/, ""), { error: (json.errors as Array<{ message?: string }>)[0]?.message ?? "GraphQL error" })
      } else {
        this.state.setState(resultTarget.replace(/^#/, ""), json.data)
      }
    } catch (err) {
      this.state.setState(resultTarget.replace(/^#/, ""), { error: String(err) })
    }
  }

  private async actionSupabaseQuery(params: Record<string, unknown>): Promise<void> {
    const baseUrl = String(params.url ?? params.supabaseUrl ?? "")
    const table = String(params.table ?? "")
    const operation = String(params.operation ?? "select")
    const apiKey = String(params.apiKey ?? params.anonKey ?? "")
    const resultTarget = String(params.resultTarget ?? `_results.${table}`)

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      apikey: apiKey,
      Authorization: `Bearer ${apiKey}`,
      Prefer: operation === "select" ? "return=representation" : "return=minimal",
    }

    let endpoint = `${baseUrl}/rest/v1/${table}`
    let method = "GET"
    let body: string | undefined

    if (operation === "insert") { method = "POST"; body = JSON.stringify(params.data) }
    else if (operation === "update") { method = "PATCH"; body = JSON.stringify(params.data) }
    else if (operation === "delete") { method = "DELETE" }

    if (operation === "select" && params.filter) {
      const filters = params.filter as Record<string, string>
      const qs = Object.entries(filters).map(([k, v]) => `${k}=${v}`).join("&")
      endpoint = `${endpoint}?${qs}`
    }

    try {
      const res = await fetch(endpoint, { method, headers, body })
      const data = await res.json()
      this.state.setState(resultTarget.replace(/^#/, ""), data)
    } catch (err) {
      this.state.setState(resultTarget.replace(/^#/, ""), { error: String(err) })
    }
  }

  private async actionSupabaseRpc(params: Record<string, unknown>): Promise<void> {
    const baseUrl = String(params.url ?? params.supabaseUrl ?? "")
    const functionName = String(params.functionName ?? params.function ?? "")
    const apiKey = String(params.apiKey ?? params.anonKey ?? "")
    const args = (params.args as Record<string, unknown>) ?? {}
    const resultTarget = String(params.resultTarget ?? "_results.rpc")

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      apikey: apiKey,
      Authorization: `Bearer ${apiKey}`,
    }

    try {
      const res = await fetch(`${baseUrl}/rest/v1/rpc/${functionName}`, {
        method: "POST",
        headers,
        body: JSON.stringify(args),
      })
      const data = await res.json()
      this.state.setState(resultTarget.replace(/^#/, ""), data)
    } catch (err) {
      this.state.setState(resultTarget.replace(/^#/, ""), { error: String(err) })
    }
  }

  private actionSetVariable(params: Record<string, unknown>): void {
    const name = String(params.name ?? params.key ?? "")
    const value = params.value
    if (name) {
      this.state.setState(`variables.${name}`, value)
    }
  }

  // ---- Batch 2: Flow Control Actions ----

  private actionLog(params: Record<string, unknown>): void {
    const level = String(params.level ?? "log")
    const message = params.message ?? params.data ?? ""
    switch (level) {
      case "warn": console.warn("[Cogo]", message); break
      case "error": console.error("[Cogo]", message); break
      default: console.log("[Cogo]", message)
    }
  }

  /**
   * Evaluate a compute expression and store the result at the target path.
   * Used by "expression" type steps with action "compute".
   * Expressions like "!#ui.items || #ui.items.length == 0" are evaluated
   * by the ComputeEvaluator and the boolean/value result is stored.
   */
  private actionCompute(params: Record<string, unknown>): void {
    const target = String(params.target ?? "").replace(/^#/, "")
    const expression = String(params.expression ?? "")
    if (!target || !expression) return
    const result = this.compute.evaluate(expression)
    this.state.setState(target, result)
  }

  private actionStopFlow(): never {
    throw { __stopFlow: true }
  }

  private actionBatch(params: Record<string, unknown>): void {
    const actions = (params.actions ?? []) as Array<{ action: string; params?: Record<string, unknown> }>
    for (const sub of actions) {
      const handler = this.builtinActions[sub.action]
      if (handler) handler(sub.params ?? {})
    }
  }

  private async actionConditionalDelay(params: Record<string, unknown>): Promise<void> {
    const condition = params.condition
    if (condition) {
      const condStr = String(condition)
      const result = this.compute.evaluate(condStr)
      if (result) {
        const ms = Number(params.duration ?? params.ms ?? 0)
        await new Promise<void>((resolve) => setTimeout(resolve, ms))
      }
    }
  }

  private async actionCrypto(params: Record<string, unknown>): Promise<void> {
    const operation = String(params.operation ?? "sha256")
    const data = String(params.data ?? "")
    const resultTarget = String(params.resultTarget ?? "_results.crypto")

    const algoMap: Record<string, string> = {
      sha256: "SHA-256",
      sha1: "SHA-1",
      sha384: "SHA-384",
      sha512: "SHA-512",
    }

    const algo = algoMap[operation] ?? "SHA-256"
    if (this.platform) {
      try {
        const hash = await this.platform.digest(algo, data)
        this.state.setState(resultTarget.replace(/^#/, ""), hash)
      } catch (err) {
        this.state.setState(resultTarget.replace(/^#/, ""), { error: String(err) })
      }
    } else {
      // Fallback to globalThis.crypto.subtle when no platform adapter
      try {
        const encoder = new TextEncoder()
        const encoded = encoder.encode(data)
        const hashBuffer = await globalThis.crypto.subtle.digest(algo, encoded)
        const hash = Array.from(new Uint8Array(hashBuffer))
          .map((b) => b.toString(16).padStart(2, "0"))
          .join("")
        this.state.setState(resultTarget.replace(/^#/, ""), hash)
      } catch (err) {
        this.state.setState(resultTarget.replace(/^#/, ""), { error: String(err) })
      }
    }
  }

  private async actionRealtimeSync(params: Record<string, unknown>): Promise<void> {
    if (!this.platform) return
    const url = String(params.url ?? "")
    const stateTarget = String(params.stateTarget ?? "_results.realtime")

    return new Promise<void>((resolve) => {
      const es = this.platform!.createEventSource(url, (data: string) => {
        let parsed: unknown
        try { parsed = JSON.parse(data) } catch { parsed = data }
        this.state.setState(stateTarget.replace(/^#/, ""), parsed)
        es.close()
        resolve()
      })
    })
  }

  private actionAnalyticsEvent(params: Record<string, unknown>): void {
    if (!this.platform) return
    const eventName = String(params.event ?? params.name ?? "")
    const eventData = (params.data ?? params.properties ?? {}) as Record<string, unknown>
    this.platform.dispatchAnalyticsEvent(eventName, eventData)
  }

  // ---- Batch 3: Browser/DOM Actions ----

  private actionGoLink(params: Record<string, unknown>): void {
    if (!this.platform) return
    const url = String(params.url ?? "")
    const newTab = params.newTab !== false
    if (url) {
      this.platform.openExternal(url, newTab)
    }
  }

  private actionShowPopup(params: Record<string, unknown>): void {
    const title = String(params.title ?? "")
    const message = String(params.message ?? "")
    const type = String(params.type ?? "info")
    if (this.onShowPopup) {
      this.onShowPopup({ title, message, type })
    } else if (this.platform) {
      this.platform.showPopup({ title, message, type })
    }
  }

  private actionShowLoading(params: Record<string, unknown>): void {
    const show = params.show !== false
    if (this.onShowLoading) {
      this.onShowLoading(show)
    } else if (this.platform) {
      this.platform.showLoading(show)
    }
  }

  private actionStorage(params: Record<string, unknown>): void {
    if (!this.platform) return
    const operation = String(params.operation ?? "get")
    const key = String(params.key ?? "")
    const resultTarget = String(params.resultTarget ?? `_results.storage.${key}`)

    switch (operation) {
      case "get": {
        const raw = this.platform.getItem(key)
        let value: unknown = raw
        if (raw) { try { value = JSON.parse(raw) } catch { /* keep as string */ } }
        this.state.setState(resultTarget.replace(/^#/, ""), value)
        break
      }
      case "set": {
        const value = params.value
        this.platform.setItem(key, typeof value === "string" ? value : JSON.stringify(value))
        break
      }
      case "remove":
        this.platform.removeItem(key)
        break
      case "clear":
        this.platform.clearStorage()
        break
    }
  }

  private async actionCopyToClipboard(params: Record<string, unknown>): Promise<void> {
    if (!this.platform) return
    const text = String(params.text ?? params.value ?? "")
    await this.platform.copyToClipboard(text)
  }

  private async actionClipboardRead(params: Record<string, unknown>): Promise<void> {
    if (!this.platform) return
    const resultTarget = String(params.resultTarget ?? "_results.clipboard")
    try {
      const text = await this.platform.readClipboard()
      this.state.setState(resultTarget.replace(/^#/, ""), text)
    } catch {
      this.state.setState(resultTarget.replace(/^#/, ""), { error: "Clipboard read denied" })
    }
  }

  private async actionNotification(params: Record<string, unknown>): Promise<void> {
    if (!this.platform) return
    const title = String(params.title ?? "")
    const body = String(params.body ?? "")
    this.platform.showNotification(title, body)
  }

  private async actionShare(params: Record<string, unknown>): Promise<void> {
    if (!this.platform) return
    try {
      await this.platform.share({
        title: params.title as string | undefined,
        text: params.text as string | undefined,
        url: params.url as string | undefined,
      })
    } catch {
      // User cancelled or share not supported
    }
  }

  private actionPrint(): void {
    if (!this.platform) return
    this.platform.print()
  }

  private actionDownload(params: Record<string, unknown>): void {
    if (!this.platform) return
    const url = String(params.url ?? "")
    const filename = String(params.filename ?? "download")
    this.platform.download(url, filename)
  }

  private actionCsvExport(params: Record<string, unknown>): void {
    if (!this.platform) return
    const data = params.data as Record<string, unknown>[] | undefined
    if (!Array.isArray(data) || data.length === 0) return

    const columns = params.columns as Array<{ key: string; header: string }> | undefined
    const filename = String(params.filename ?? "export.csv")

    // Build header row
    const headers = columns
      ? columns.map((c) => c.header)
      : Object.keys(data[0])
    const keys = columns
      ? columns.map((c) => c.key)
      : Object.keys(data[0])

    // Escape CSV field
    const escapeField = (val: unknown): string => {
      const str = val == null ? "" : String(val)
      if (str.includes(",") || str.includes('"') || str.includes("\n")) {
        return `"${str.replace(/"/g, '""')}"`
      }
      return str
    }

    const rows = [
      headers.map(escapeField).join(","),
      ...data.map((row) => keys.map((k) => escapeField(row[k])).join(",")),
    ]
    const csv = rows.join("\n")

    // Create blob URL and trigger download
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" })
    const url = URL.createObjectURL(blob)
    this.platform.download(url, filename)
    setTimeout(() => URL.revokeObjectURL(url), 5000)
  }

  private async actionFileUpload(params: Record<string, unknown>): Promise<void> {
    if (!this.platform) return
    const accept = String(params.accept ?? "*/*")
    const resultTarget = String(params.resultTarget ?? "_results.fileUpload")

    const result = await this.platform.pickFile(accept)
    if (result) {
      this.state.setState(resultTarget.replace(/^#/, ""), result)
    }
  }

  private async actionMediaCapture(params: Record<string, unknown>): Promise<void> {
    if (!this.platform) return
    const resultTarget = String(params.resultTarget ?? "_results.mediaCapture")
    const audio = params.audio === true
    const video = params.video !== false

    try {
      const stream = await this.platform.getUserMedia({ audio, video })
      this.state.setState(resultTarget.replace(/^#/, ""), {
        success: true,
        stream,
      })
    } catch (err) {
      this.state.setState(resultTarget.replace(/^#/, ""), { error: String(err) })
    }
  }

  private async actionGeolocation(params: Record<string, unknown>): Promise<void> {
    if (!this.platform) return
    const resultTarget = String(params.resultTarget ?? "_results.geolocation")

    try {
      const pos = await this.platform.getGeolocation()
      this.state.setState(resultTarget.replace(/^#/, ""), pos)
    } catch (err) {
      this.state.setState(resultTarget.replace(/^#/, ""), { error: String(err) })
    }
  }

  // ---- Batch 4: Advanced Actions ----

  private async actionFirebaseAuth(params: Record<string, unknown>): Promise<void> {
    const action = String(params.action ?? "signIn")
    const resultTarget = String(params.resultTarget ?? "_results.firebaseAuth")

    if (this.onFirebaseAuth) {
      const result = await this.onFirebaseAuth({
        action,
        email: params.email as string | undefined,
        password: params.password as string | undefined,
        token: params.token as string | undefined,
      })
      this.state.setState(resultTarget.replace(/^#/, ""), result)
    } else {
      this.state.setState(resultTarget.replace(/^#/, ""), { success: false, error: "Firebase auth not configured" })
    }
  }

  private async actionFirestore(params: Record<string, unknown>): Promise<void> {
    const operation = String(params.operation ?? "get")
    const collection = String(params.collection ?? "")
    const docId = params.docId as string | undefined
    const data = params.data as Record<string, unknown> | undefined
    const resultTarget = String(params.resultTarget ?? "_results.firestore")

    if (this.onFirestore) {
      const result = await this.onFirestore({ operation, collection, docId, data })
      this.state.setState(resultTarget.replace(/^#/, ""), result)
    } else {
      this.state.setState(resultTarget.replace(/^#/, ""), { success: false, error: "Firestore not configured" })
    }
  }

  private async actionQueue(params: Record<string, unknown>): Promise<void> {
    const queueName = String(params.queue ?? "default")
    const actionName = params.action as string | undefined
    const actionParams = (params.params as Record<string, unknown>) ?? {}

    if (!actionName) return

    if (!this.actionQueues.has(queueName)) {
      this.actionQueues.set(queueName, [])
    }
    const queue = this.actionQueues.get(queueName)!

    return new Promise<void>((resolve) => {
      queue.push(async () => {
        const handler = this.builtinActions[actionName]
        if (handler) await handler(actionParams)
        resolve()
      })

      // Process if this is the only item (queue was empty before push)
      if (queue.length === 1) {
        const processQueue = async () => {
          while (queue.length > 0) {
            const task = queue.shift()!
            await task()
          }
        }
        processQueue()
      }
    })
  }

  private async actionRetry(params: Record<string, unknown>): Promise<void> {
    const actionName = params.action as string | undefined
    const actionParams = (params.params as Record<string, unknown>) ?? {}
    const maxRetries = Number(params.maxRetries ?? 3)
    const delayMs = Number(params.delay ?? 1000)

    if (!actionName) return
    const handler = this.builtinActions[actionName]
    if (!handler) return

    for (let i = 0; i <= maxRetries; i++) {
      try {
        await handler(actionParams)
        return
      } catch (e) {
        if (i === maxRetries) throw e
        await new Promise<void>((r) => setTimeout(r, delayMs * (i + 1)))
      }
    }
  }

  private async actionThrottle(params: Record<string, unknown>): Promise<void> {
    const key = String(params.key ?? "default")
    const intervalMs = Number(params.interval ?? 1000)
    const actionName = params.action as string | undefined
    const actionParams = (params.params as Record<string, unknown>) ?? {}

    if (!actionName) return

    const now = Date.now()
    const last = this.throttleTimestamps.get(key) ?? 0
    if (now - last < intervalMs) return

    this.throttleTimestamps.set(key, now)
    const handler = this.builtinActions[actionName]
    if (handler) await handler(actionParams)
  }

  private async actionDebounce(params: Record<string, unknown>): Promise<void> {
    const key = String(params.key ?? "default")
    const delayMs = Number(params.delay ?? 300)
    const actionName = params.action as string | undefined
    const actionParams = (params.params as Record<string, unknown>) ?? {}

    if (!actionName) return

    const existing = this.actionDebounceTimers.get(key)
    if (existing) clearTimeout(existing)

    return new Promise<void>((resolve) => {
      this.actionDebounceTimers.set(
        key,
        setTimeout(async () => {
          this.actionDebounceTimers.delete(key)
          const handler = this.builtinActions[actionName]
          if (handler) await handler(actionParams)
          resolve()
        }, delayMs),
      )
    })
  }

  private async actionTransaction(params: Record<string, unknown>): Promise<void> {
    const actions = (params.actions ?? []) as Array<{ action: string; params?: Record<string, unknown> }>
    const rollbackActions = (params.rollback ?? []) as Array<{ action: string; params?: Record<string, unknown> }>
    const resultTarget = String(params.resultTarget ?? "_results.transaction")

    const results: Array<{ action: string; status: string }> = []
    try {
      for (const sub of actions) {
        const handler = this.builtinActions[sub.action]
        if (handler) {
          await handler(sub.params ?? {})
          results.push({ action: sub.action, status: "success" })
        }
      }
      this.state.setState(resultTarget.replace(/^#/, ""), { success: true, results })
    } catch (e) {
      // Execute rollback
      for (const rb of rollbackActions) {
        const handler = this.builtinActions[rb.action]
        if (handler) {
          try { await handler(rb.params ?? {}) } catch { /* ignore rollback errors */ }
        }
      }
      this.state.setState(resultTarget.replace(/^#/, ""), { success: false, error: String(e), results })
    }
  }

  /** Clean up all action queues, throttle timestamps, and debounce timers. */
  clearActionTimers(): void {
    this.actionQueues.clear()
    this.throttleTimestamps.clear()
    for (const timer of this.actionDebounceTimers.values()) clearTimeout(timer)
    this.actionDebounceTimers.clear()
  }

  // ---- Batch 5: AI / Gemini Actions ----

  private async actionGemini(params: Record<string, unknown>): Promise<void> {
    const apiKey = String(params.apiKey ?? '')
    const model = String(params.model ?? 'gemini-2.5-pro')
    const prompt = String(params.prompt ?? '')
    const exposedActions = (params.exposedActions as string[]) ?? []
    const resultTarget = String(params.resultTarget ?? '_results.gemini')
    const maxToolCalls = Number(params.maxToolCalls ?? 5)

    const { GeminiClient } = await import('./gemini/gemini-client')
    const { ToolCallbackHandler } = await import('./gemini/tool-callback')
    const { buildFunctionDeclarations } = await import('./gemini/function-declaration')

    const client = new GeminiClient({ apiKey, model })
    const callbackHandler = new ToolCallbackHandler(
      async (actionName, actionParams) => {
        const action = this.builtinActions[actionName]
        if (!action) throw new Error(`Unknown action: ${actionName}`)
        await action(actionParams)
        return this.state.get(`_results.${actionName}`) ?? {}
      },
    )

    const tools = exposedActions.length > 0
      ? [{ functionDeclarations: buildFunctionDeclarations(exposedActions) }]
      : undefined

    const history = (this.state.get('_gemini.history') as GeminiContent[]) ?? []
    const contents: GeminiContent[] = [
      ...history,
      { role: 'user', parts: [{ text: prompt }] },
    ]

    let currentContents: GeminiContent[] = contents
    let toolCallCount = 0

    while (toolCallCount < maxToolCalls) {
      const result = await client.generateContent({
        contents: currentContents,
        ...(tools && { tools }),
      })

      if (result.functionCall) {
        toolCallCount++
        const actionResult = await callbackHandler.handle(result.functionCall)
        const functionResponseContent = callbackHandler.buildFunctionResponseContent(
          actionResult.name,
          actionResult.response,
        )
        currentContents = [
          ...currentContents,
          { role: 'model', parts: [{ functionCall: result.functionCall }] } as GeminiContent,
          functionResponseContent,
        ]
      } else {
        this.state.setState('_gemini.history', currentContents)
        this.state.setState(resultTarget.replace(/^#/, ''), {
          type: 'text',
          response: result.text ?? '',
          toolCallsUsed: toolCallCount,
        })
        return
      }
    }

    this.state.setState(resultTarget.replace(/^#/, ''), {
      type: 'error',
      error: `Max tool calls (${maxToolCalls}) reached`,
      toolCallsUsed: toolCallCount,
    })
  }
}
